import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../services/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { saveUserPreferences, getUserPreferences, getUserData } from '../services/database';
import AppNavBar from './AppNavBar';
import { useModal } from '../contexts/ModalContext';

const MyInfoPage = () => {
  const navigate = useNavigate();
  const { showAlert } = useModal();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        try {
          const data = await getUserData(firebaseUser.uid);
          setUserData(data);
        } catch (error) {
          console.error('Error loading user data:', error);
        }
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const [formData, setFormData] = useState({
    subject: '',
    customSubject: '',
    level: '',
    country: '',
    customCountry: '',
    productUpdates: false,
    newsletter: false,
    webinarsTraining: false
  });

  const [isSaving, setIsSaving] = useState(false);
  const [preferencesLoading, setPreferencesLoading] = useState(true);

  const subjects = [
    'Mathematics', 'English', 'Science', 'History', 'Geography',
    'Art', 'Music', 'Physical Education', 'Modern Languages',
    'Religious Studies', 'Computing/ICT', 'Business Studies', 'Other'
  ];

  const countries = [
    'Afghanistan', 'Albania', 'Algeria', 'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan',
    'Bahrain', 'Bangladesh', 'Belarus', 'Belgium', 'Bolivia', 'Bosnia and Herzegovina', 'Brazil', 'Bulgaria',
    'Cambodia', 'Canada', 'Chile', 'China', 'Colombia', 'Costa Rica', 'Croatia', 'Cyprus', 'Czech Republic',
    'Denmark', 'Dominican Republic', 'Ecuador', 'Egypt', 'Estonia', 'Ethiopia', 'Finland', 'France',
    'Georgia', 'Germany', 'Ghana', 'Greece', 'Guatemala', 'Hungary', 'Iceland', 'India', 'Indonesia',
    'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kuwait',
    'Latvia', 'Lebanon', 'Lithuania', 'Luxembourg', 'Malaysia', 'Malta', 'Mexico', 'Morocco', 'Netherlands',
    'New Zealand', 'Nigeria', 'Norway', 'Oman', 'Pakistan', 'Panama', 'Peru', 'Philippines', 'Poland',
    'Portugal', 'Qatar', 'Romania', 'Russia', 'Saudi Arabia', 'Serbia', 'Singapore', 'Slovakia', 'Slovenia',
    'South Africa', 'South Korea', 'Spain', 'Sri Lanka', 'Sweden', 'Switzerland', 'Taiwan', 'Thailand',
    'Tunisia', 'Turkey', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay',
    'Venezuela', 'Vietnam', 'Other'
  ];

  useEffect(() => {
    const loadPreferences = async () => {
      if (loading || !user) return;
      try {
        const preferences = await getUserPreferences(user.uid);
        if (preferences) setFormData(preferences);
      } catch (error) {
        console.error('Error loading preferences:', error);
      }
      setPreferencesLoading(false);
    };
    loadPreferences();
  }, [user, loading]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (field) => {
    setFormData(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSave = async () => {
    if (!user?.uid) {
      await showAlert('You must be logged in to save preferences', 'Error');
      return;
    }
    setIsSaving(true);
    try {
      await saveUserPreferences(user.uid, formData);
      await showAlert('Preferences saved successfully!', 'Saved');
    } catch (error) {
      console.error('Error saving preferences:', error);
      await showAlert('Error saving preferences: ' + error.message, 'Error');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
        <AppNavBar currentPage="my-info" />
        <div className="flex items-center justify-center py-12">
          <div className="bg-white rounded-lg border-4 border-ink-900 shadow-brutal p-12 w-full max-w-2xl mx-4 text-center">
            <h2 className="text-3xl font-bold text-ink-800">Loading...</h2>
            <p className="text-ink-600 mt-3 text-lg">Checking authentication</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
        <AppNavBar currentPage="my-info" />
        <div className="flex items-center justify-center py-12">
          <div className="bg-white rounded-lg border-4 border-ink-900 shadow-brutal p-12 w-full max-w-2xl mx-4 text-center">
            <h2 className="text-3xl font-bold text-ink-800">Please log in...</h2>
            <p className="text-ink-600 mt-3 text-lg">You need to be logged in to access this page</p>
            <button
              onClick={() => navigate('/login')}
              className="mt-6 bg-coral-300 text-white py-2 px-6 rounded-lg hover:bg-coral-500 transition duration-200"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isSubscribed = userData?.subscriptionStatus === 'active' || userData?.subscriptionTier === 'premium' || userData?.subscriptionTier === 'premiumPlus';

  return (
    <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
      <AppNavBar currentPage="my-info" />
      <div className="flex items-center justify-center py-12">
        <div className="bg-white rounded-lg border-4 border-ink-900 shadow-brutal p-12 w-full max-w-2xl mx-4">

          <div className="text-center mb-8">
          <h2 className="text-4xl md:text-5xl font-bold text-coral-500 leading-tight drop-shadow-[2px_2px_0_rgba(0,0,0,1)]">Account Management</h2>
            <p className="text-ink-600 mt-3 text-lg">
              Tell us about yourself to personalise your TeAIch experience
            </p>
          </div>

          {preferencesLoading ? (
            <div className="text-center py-8">
              <p className="text-ink-600">Loading your preferences...</p>
            </div>
          ) : (
            <div className="max-w-md mx-auto space-y-6">

              {/* Subject */}
              <div>
                <label className="block text-ink-700 text-sm font-bold mb-3">SUBJECT YOU TEACH</label>
                <select
                  value={formData.subject}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  className="w-full px-4 py-3 border border-sand-300 rounded-lg focus:outline-none focus:border-coral-500 text-lg"
                >
                  <option value="">Select your subject</option>
                  {subjects.map(subject => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>
                {formData.subject === 'Other' && (
                  <input
                    type="text"
                    value={formData.customSubject}
                    onChange={(e) => handleInputChange('customSubject', e.target.value)}
                    placeholder="Please specify the subject you teach"
                    className="w-full px-4 py-3 border border-sand-300 rounded-lg focus:outline-none focus:border-coral-500 text-lg mt-3"
                  />
                )}
              </div>

              {/* Level */}
              <div>
                <label className="block text-ink-700 text-sm font-bold mb-3">LEVEL YOU TEACH</label>
                <div className="grid grid-cols-1 gap-3">
                  {['Primary', 'Secondary', 'College/University'].map(level => (
                    <label key={level} className="flex items-center p-3 border border-sand-300 rounded-lg cursor-pointer hover:bg-sand-50 transition-colors">
                      <input
                        type="radio"
                        name="level"
                        value={level}
                        checked={formData.level === level}
                        onChange={(e) => handleInputChange('level', e.target.value)}
                        className="w-4 h-4 text-coral-600 focus:ring-coral-500 mr-3"
                      />
                      <span className="text-lg text-ink-700 font-medium">{level}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Country */}
              <div>
                <label className="block text-ink-700 text-sm font-bold mb-3">COUNTRY</label>
                <select
                  value={formData.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  className="w-full px-4 py-3 border border-sand-300 rounded-lg focus:outline-none focus:border-coral-500 text-lg"
                >
                  <option value="">Select your country</option>
                  {countries.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
                {formData.country === 'Other' && (
                  <input
                    type="text"
                    value={formData.customCountry}
                    onChange={(e) => handleInputChange('customCountry', e.target.value)}
                    placeholder="Please specify your country"
                    className="w-full px-4 py-3 border border-sand-300 rounded-lg focus:outline-none focus:border-coral-500 text-lg mt-3"
                  />
                )}
              </div>

              {/* Communication Preferences */}
              <div>
                <label className="block text-ink-700 text-sm font-bold mb-3">COMMUNICATION PREFERENCES</label>
                <div className="grid grid-cols-1 gap-3">
                  {[
                    { field: 'productUpdates', label: 'Product Updates', desc: 'Receive updates about new features, improvements, and bug fixes' },
                    { field: 'newsletter', label: 'Newsletter', desc: 'Stay informed about education trends, tips, and TeAIch news' },
                    { field: 'webinarsTraining', label: 'Webinars & Training', desc: 'Get notified about workshops, training sessions, and educational webinars' },
                  ].map(({ field, label, desc }) => (
                    <label key={field} className="flex items-start p-3 border border-sand-300 rounded-lg cursor-pointer hover:bg-sand-50 transition-colors">
                      <input
                        type="checkbox"
                        checked={formData[field]}
                        onChange={() => handleCheckboxChange(field)}
                        className="w-4 h-4 text-coral-600 focus:ring-coral-500 mt-1 mr-3"
                      />
                      <div>
                        <span className="text-lg text-ink-700 font-medium">{label}</span>
                        <p className="text-sm text-ink-500">{desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Subscription Management */}
              <div className="pt-4 border-t border-sand-200">
                <label className="block text-ink-700 text-sm font-bold mb-3">SUBSCRIPTION</label>
                <div className="bg-sand-50 p-4 rounded-lg mb-4">
                  <div className="flex justify-between items-center mb-3">
                    <span className="font-semibold text-ink-700">Current Plan:</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      isSubscribed ? 'bg-coral-100 text-coral-800' : 'bg-sand-200 text-ink-600'
                    }`}>
                      {isSubscribed ? 'TeAIch Unlimited' : 'No Active Subscription'}
                    </span>
                  </div>
                  {isSubscribed && (
                    <div className="text-sm text-ink-600 space-y-1">
                      <div>📊 Markings this month: {userData?.assessUsage || 0} (Unlimited)</div>
                      <div>📝 Lesson plans this month: {userData?.plannerUsage || 0} (Unlimited)</div>
                      {userData?.lastPaymentDate && (
                        <div className="text-ink-500 pt-1">
                          Last payment: {new Date(userData.lastPaymentDate.seconds * 1000).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  )}
                  {!isSubscribed && (
                    <p className="text-sm text-ink-500">Subscribe to get unlimited access to all TeAIch tools.</p>
                  )}
                </div>

                <div className="space-y-3">
                  {!isSubscribed && (
                    <button
                      onClick={() => navigate('/pricing')}
                      className="w-full bg-coral-300 text-white py-3 px-4 rounded-lg hover:bg-coral-500 transition duration-200 text-center font-semibold"
                    >
                      🚀 Subscribe — £8.99/month
                    </button>
                  )}
                  {isSubscribed && (
                    <button
                      onClick={() => navigate('/cancel-membership')}
                      className="w-full text-red-600 hover:text-red-800 hover:bg-red-50 py-2 px-4 rounded-lg transition duration-200 text-center underline"
                    >
                      Cancel Membership
                    </button>
                  )}
                </div>
              </div>

              {/* Save */}
              <div className="pt-4">
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="w-full bg-coral-300 text-white py-3 px-6 rounded-lg hover:bg-coral-500 transition duration-200 text-lg font-semibold disabled:opacity-50"
                >
                  {isSaving ? 'Saving...' : 'Save Preferences'}
                </button>
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyInfoPage;